import classNames from "classnames";
import * as React from "react";

import CopyToClipboardButton from "@/shared/components/CopyToClipboardButton";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { FormattedDateTime } from "@/shared/datetime";
import type { MetricRecord } from "@/shared/domain/metrics";
import { useMetricDefinition } from "@/shared/domain/metrics/hooks";
import { latestRecord } from "@/shared/domain/metrics/latestRecord";
import { EntityIcon, X } from "@/shared/foundation/icons";
import { RobotoButton, Spinner } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation/Goto";
import { Principal } from "@/shared/principal";

import { withUnit } from "./formatMetricValue";
import styles from "./MetricDetailCard.module.css";

export interface MetricDetailCardProps {
  /** The selected metric's full dotted name. */
  name: string;
  /** Every record published under `name`; at least one. */
  records: MetricRecord[];
  /** Current org id — scopes the definition lookup that yields the description. */
  orgId: string;
  /** Dismiss the card and clear the page's selection. */
  onClose: () => void;
  className?: string;
  /**
   * The id for the card's heading when the card fills a dialog: the dialog
   * is named by the heading and starts focus on it, so the card is not a
   * landmark of its own.
   */
  dialogHeadingId?: string;
}

/**
 * Details of the selected metric, beside the session-metrics table (in a
 * dialog over it on a narrow page). Top to
 * bottom: the metric's name, its newest value in full precision, when and by
 * whom that value was published, the invocation and device it came from, and
 * its description. A field the metric has no value for is left out.
 *
 * The value, timestamp and attribution all come from the most recently
 * published record. The description lives on the metric *definition* (not
 * the record), so it is fetched lazily by name while the card is mounted.
 */
export function MetricDetailCard({
  name,
  records,
  orgId,
  onClose,
  className,
  dialogHeadingId,
}: MetricDetailCardProps) {
  const latest = latestRecord(records);
  const definition = useMetricDefinition(name, orgId);
  const description = definition.data?.description?.trim();
  const Container = dialogHeadingId === undefined ? "aside" : "div";

  return (
    <Container
      className={classNames(styles.card, className)}
      aria-label={
        dialogHeadingId === undefined ? `Metric detail for ${name}` : undefined
      }
    >
      <div className={styles.header}>
        <MetricNameHeading name={name} id={dialogHeadingId} />
        <RobotoButton
          variant="ghost"
          size="sm"
          aria-label="Close"
          icon={<X size={16} aria-hidden />}
          onClick={onClose}
        />
      </div>

      {latest ? (
        <dl className={styles.facts}>
          <div className={styles.fact}>
            <dt>Value</dt>
            <dd className={styles.valueLine}>
              <code className={styles.value}>
                {withUnit(String(latest.value), latest.unit)}
              </code>
              <CopyToClipboardButton
                text={String(latest.value)}
                ariaLabel="Copy value"
                className={styles.copy}
              />
            </dd>
          </div>
          <div className={styles.fact}>
            <dt>Last published</dt>
            <dd>
              <FormattedDateTime
                value={latest.published}
                time="seconds"
                timeZone
              />
            </dd>
          </div>
          {latest.published_by ? (
            <div className={styles.fact}>
              <dt>Published by</dt>
              <dd>
                {Principal.fromString(latest.published_by).ok?.unqualifiedId ??
                  latest.published_by}
              </dd>
            </div>
          ) : null}
          {latest.invocation_id ? (
            <div className={styles.fact}>
              <dt>Invocation</dt>
              <dd className={styles.linkLine}>
                <EntityIcon type="invocation" size={14} />
                <RobotoLinkHoverUnderline
                  to={Goto.createInvocationRoute(latest.invocation_id)}
                  className={styles.mono}
                >
                  {latest.invocation_id}
                </RobotoLinkHoverUnderline>
              </dd>
            </div>
          ) : null}
          {latest.device_id ? (
            <div className={styles.fact}>
              <dt>Device</dt>
              <dd className={styles.linkLine}>
                <EntityIcon type="device" size={14} />
                <RobotoLinkHoverUnderline
                  to={Goto.createDeviceMetricsRoute(latest.device_id)}
                >
                  {latest.device_id}
                </RobotoLinkHoverUnderline>
              </dd>
            </div>
          ) : null}
          {description ? (
            <div className={styles.fact}>
              <dt>Description</dt>
              <dd>{description}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}

      {definition.isLoading ? (
        <Spinner size="sm" aria-label="Loading description" />
      ) : null}
    </Container>
  );
}

/**
 * The card's title: the metric's last name part on its own line, under the
 * parts before it. The parent path breaks only after a dot, so a long one
 * wraps between segments, not inside them. Read out as the full name. With
 * an `id`, it names a dialog and takes the dialog's first focus.
 */
function MetricNameHeading({ name, id }: { name: string; id?: string }) {
  const lastDot = name.lastIndexOf(".");
  const parent = lastDot === -1 ? "" : name.slice(0, lastDot);
  const leaf = name.slice(lastDot + 1);
  return (
    <h2
      id={id}
      tabIndex={id === undefined ? undefined : -1}
      className={styles.name}
    >
      <span className="sr-only">{name}</span>
      {parent ? (
        <span className={styles.parentPath} aria-hidden="true">
          {parent.split(".").map((segment, index, segments) => (
            <React.Fragment key={index}>
              {segment}
              {index < segments.length - 1 ? (
                <>
                  .<wbr />
                </>
              ) : null}
            </React.Fragment>
          ))}
        </span>
      ) : null}
      <span className={styles.leafName} aria-hidden="true">
        {leaf}
      </span>
    </h2>
  );
}
